import React, { useMemo, useState } from "react";
import Layout from "../components/Layout";
import InfoTooltip from "../components/InfoTooltip";
import SEO from "../components/SEO";
import { submitServicesInquiry } from "../lib/api";
import { sendInquiryEmail } from "../lib/emailjs";

const serviceCards = [
  {
    title: "Shop Drawing",
    desc: "Curtain Wall / Unitized Systems detailed for fabrication.",
  },
  {
    title: "Sliding & Folding Doors",
    desc: "System drawings and detailing for moving facade openings.",
  },
  {
    title: "ACP Cladding & Composite Panels",
    desc: "Layout, panelization, and fixing details for composite skins.",
  },
  {
    title: "Structural Glazing",
    desc: "Point-fixed and frame-supported structural glass systems.",
  },
  {
    title: "Windows, Skylights & Specialty Enclosures",
    desc: "Schedules, sections, and assembly details for openings.",
  },
  {
    title: "Spider Glazing Systems",
    desc: "Spider, SS bracket, and patch-fitting glazed assemblies.",
  },
];

// Pre-filled rate rows. asPerScope rows skip math.
const defaultRows = [
  { group: "SHOP DRAWINGS", isGroup: true },
  { service: "Curtain Wall / Unitized", unit: "unit", our_rate: 25, their_rate: 30, notes: "" },
  { service: "Sliding & Folding Doors", unit: "unit", our_rate: 20, their_rate: 25, notes: "" },
  { service: "ACP / Composite Panels", unit: "unit", our_rate: 18, their_rate: 22, notes: "" },
  { service: "Structural Glazing", unit: "unit", our_rate: 22, their_rate: 28, notes: "" },
  { service: "Spider Glazing", unit: "unit", our_rate: 25, their_rate: 32, notes: "" },
  { service: "Window Schedule", unit: "unit", our_rate: 15, their_rate: 20, notes: "" },
  { service: "Skylight Drawing", unit: "unit", our_rate: 20, their_rate: 26, notes: "" },
  { service: "DWG Conversion / Redraw", unit: "unit", our_rate: 10, their_rate: 14, notes: "" },

  { group: "MTO — MATERIAL TAKE-OFF", isGroup: true },
  { service: "MTO Curtain Wall", unit: "unit", our_rate: 18, their_rate: 24, notes: "" },
  { service: "MTO SS Spider", unit: "unit", our_rate: 10, their_rate: 14, notes: "" },
  { service: "MTO Spider Glazing", unit: "unit", our_rate: 15, their_rate: 20, notes: "" },
  { service: "MTO Aluminium ACP Fins", unit: "unit", our_rate: 10, their_rate: 14, notes: "" },
  { service: "MTO Louvers / Pergolas", unit: "unit", our_rate: 12, their_rate: 18, notes: "" },
  { service: "MTO MS Structure", unit: "unit", our_rate: 8, their_rate: 12, notes: "" },

  { group: "MODELING", isGroup: true },
  {
    service: "Revit BIM / 3D Modeling",
    unit: "—",
    our_rate: null,
    their_rate: null,
    asPerScope: true,
    notes: "As Per Scope",
  },
];

const buildInitialRows = () =>
  defaultRows.map((r, idx) => ({
    id: `row-${idx}`,
    ...r,
    quantity: 0,
    custom: false,
  }));

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

const Services = () => {
  const [rows, setRows] = useState(buildInitialRows);
  const [form, setForm] = useState({
    organization_name: "",
    contact_person: "",
    contact_number: "",
    email_address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const updateRow = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addCustomRow = () => {
    setRows((rs) => [
      ...rs,
      {
        id: `row-custom-${Date.now()}`,
        service: "",
        unit: "unit",
        our_rate: 0,
        their_rate: 0,
        quantity: 0,
        notes: "",
        custom: true,
      },
    ]);
  };

  const removeRow = (id) => {
    setRows((rs) => rs.filter((r) => r.id !== id));
  };

  const { totalProposed, lineItems } = useMemo(() => {
    let proposed = 0;
    const items = [];
    rows.forEach((r) => {
      if (r.isGroup) return;
      const qty = Number(r.quantity) || 0;
      const proposedRate = Number(r.their_rate) || 0;
      // Only Proposed Total is calculated. Base rate is informational only.
      const proposedTotal = r.asPerScope ? 0 : qty * proposedRate;
      proposed += proposedTotal;
      if (qty > 0 || proposedRate > 0 || r.custom) {
        items.push({
          service: r.service,
          unit: r.unit || "",
          our_rate: Number(r.our_rate) || 0,
          their_rate: proposedRate,
          quantity: qty,
          our_total: 0,
          their_total: proposedTotal,
          notes: r.notes || "",
        });
      }
    });
    return { totalProposed: proposed, lineItems: items };
  }, [rows]);

  const onChangeForm = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const resetAll = () => {
    setRows(buildInitialRows());
    setForm({
      organization_name: "",
      contact_person: "",
      contact_number: "",
      email_address: "",
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !form.organization_name.trim() ||
      !form.contact_person.trim() ||
      !form.contact_number.trim() ||
      !form.email_address.trim()
    ) {
      setError("Please complete all contact fields before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: lineItems,
        total_our_rate: 0,
        total_their_rate: totalProposed,
      };
      const saved = await submitServicesInquiry(payload);
      // Owner Gmail notification via EmailJS.
      // Build a short notes string summarising the line items (or "—" if empty).
      const notesSummary =
        lineItems.length > 0
          ? lineItems
              .map(
                (it) =>
                  `${it.service} × ${it.quantity} ${it.unit || ""} @ ₹${it.their_rate} = ₹${it.their_total}`
              )
              .join("; ") +
            `  |  Proposed Total: ₹${totalProposed.toFixed(2)}`
          : "No line items selected.";
      const emailResult = await sendInquiryEmail({
        page: "Lancers Services",
        organization: form.organization_name,
        contact_person: form.contact_person,
        phone: form.contact_number,
        email: form.email_address,
        reason: notesSummary,
      });
      const log = JSON.parse(
        localStorage.getItem("ls_services_inquiries") || "[]"
      );
      log.unshift(saved);
      localStorage.setItem("ls_services_inquiries", JSON.stringify(log));
      // eslint-disable-next-line no-console
      console.log("[Lancers Services] inquiry submitted:", saved, emailResult);
      setSubmitted(true);
      setTimeout(() => {
        resetAll();
      }, 400);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Services inquiry failed:", err);
      setError("Something went wrong. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout footerNote="© 2026 Lancers Studio. Outsourced technical support for facade industry drafting, MTO, and scope-based modeling work.">
      <SEO
        path="/services"
        title="Lancers Services — Facade Drafting, MTO & BIM Outsourcing | Lancers Studio"
        description="Outsourced shop drawings, material take-off and Revit BIM modeling for curtain wall, unitized, ACP, structural and spider glazing systems. Get a tentative rate online."
      />
      <section
        className="relative px-5 sm:px-8 pt-14 pb-10 max-w-7xl mx-auto"
        data-testid="services-section"
      >
        <div
          className="ls-glow"
          style={{ top: "-180px", left: "-160px", opacity: 0.7 }}
        />

        {/* TOP — Overview */}
        <div className="ls-card-strong px-6 sm:px-10 py-10 ls-fade-up" data-testid="services-overview">
          <span className="ls-eyebrow">Outsourced Technical Support</span>
          <h1
            className="font-cinzel gold-text mt-5 leading-tight"
            style={{
              fontSize: "clamp(2.4rem, 5vw, 4rem)",
              letterSpacing: "0.06em",
            }}
            data-testid="services-title"
          >
            Lancers Services
          </h1>
          <p
            className="mt-6 text-base sm:text-lg leading-relaxed max-w-3xl"
            style={{ color: "var(--ls-muted)" }}
            data-testid="services-intro"
          >
            Lancers Services is the project execution arm of Lancers Studio. We
            handle outsourced technical work for the facade industry — from shop
            drawing preparation to material take-off and BIM modeling.
          </p>

          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="services-grid"
          >
            {serviceCards.map((s, i) => (
              <div
                key={s.title}
                className="ls-card px-5 py-6 transition hover:-translate-y-1 hover:border-white/20"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`service-card-${i}`}
              >
                <h3
                  className="font-cinzel gold-text mb-2"
                  style={{
                    fontSize: "1rem",
                    letterSpacing: "0.12em",
                  }}
                >
                  {s.title}
                </h3>
                <p
                  className="text-[0.88rem] leading-relaxed"
                  style={{ color: "var(--ls-muted)" }}
                >
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* RATE TABLE */}
        <div className="ls-card-strong mt-12 px-6 sm:px-10 py-10 ls-fade-up" data-testid="rate-table-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2
                className="font-cinzel gold-text"
                style={{
                  fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                  letterSpacing: "0.08em",
                }}
                data-testid="rate-table-title"
              >
                Service Rate Reference
              </h2>
              <p
                className="mt-2 text-sm max-w-2xl"
                style={{ color: "var(--ls-muted)" }}
              >
                Rates shown are indicative. Enter quantities to calculate your
                tentative estimate.
              </p>
            </div>
            <button
              type="button"
              onClick={addCustomRow}
              className="ls-btn-ghost"
              data-testid="add-item-btn"
            >
              + Add Item
            </button>
          </div>

          <div className="mt-6 overflow-x-auto ls-scroll" data-testid="rate-table-wrap">
            <table className="ls-table" data-testid="rate-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Unit</th>
                  <th>
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      Rate (₹)
                      <InfoTooltip
                        ariaLabel="About the Rate (₹) column"
                        label="Reference rate only. Proposed total is calculated using your proposed rate."
                        testId="rate-info-tooltip"
                      />
                    </span>
                  </th>
                  <th>Quantity</th>
                  <th>Proposed Rate</th>
                  <th>Proposed Total</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  if (r.isGroup) {
                    return (
                      <tr key={r.id} className="group-header">
                        <td colSpan={8}>{r.group}</td>
                      </tr>
                    );
                  }
                  const qty = Number(r.quantity) || 0;
                  const proposedRate = Number(r.their_rate) || 0;
                  const proposedTotal = r.asPerScope ? null : qty * proposedRate;
                  // Numeric guard — strip negatives and non-numerics, allow decimals.
                  const clean = (val, allowDecimal) => {
                    if (val === "" || val === null || val === undefined) return "";
                    let s = String(val).replace(/[^0-9.]/g, "");
                    if (!allowDecimal) s = s.replace(/\./g, "");
                    // collapse multiple dots
                    const parts = s.split(".");
                    if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
                    return s;
                  };
                  return (
                    <tr key={r.id} data-testid={`rate-row-${r.id}`}>
                      <td>
                        {r.custom ? (
                          <input
                            className="ls-table-input"
                            style={{ width: 200 }}
                            placeholder="Service name"
                            value={r.service}
                            onChange={(e) =>
                              updateRow(r.id, { service: e.target.value })
                            }
                            data-testid={`row-service-${r.id}`}
                          />
                        ) : (
                          <span style={{ color: "#eaeaea" }}>{r.service}</span>
                        )}
                      </td>
                      <td>
                        {r.custom ? (
                          <input
                            className="ls-table-input"
                            style={{ width: 70 }}
                            value={r.unit}
                            onChange={(e) =>
                              updateRow(r.id, { unit: e.target.value })
                            }
                          />
                        ) : (
                          r.unit
                        )}
                      </td>
                      {/* Rate (₹) — READ-ONLY. Base rate is informational only. */}
                      <td>
                        {r.asPerScope ? (
                          <span style={{ color: "#e6c168" }}>As Per Scope</span>
                        ) : r.custom ? (
                          <span style={{ color: "var(--ls-muted-2)" }}>—</span>
                        ) : (
                          <span style={{ color: "#f3d789", fontWeight: 500 }}>
                            ₹{r.our_rate}
                          </span>
                        )}
                      </td>
                      {/* Quantity — editable, integer ≥ 0 */}
                      <td>
                        {r.asPerScope ? (
                          <span style={{ color: "var(--ls-muted-2)" }}>—</span>
                        ) : (
                          <input
                            className="ls-table-input"
                            type="number"
                            inputMode="numeric"
                            min="0"
                            step="1"
                            placeholder="Enter Qty"
                            value={r.quantity === 0 ? "" : r.quantity}
                            onChange={(e) =>
                              updateRow(r.id, {
                                quantity: clean(e.target.value, false),
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                            }}
                            data-testid={`row-qty-${r.id}`}
                          />
                        )}
                      </td>
                      {/* Proposed Rate — editable on ALL rows, decimals allowed */}
                      <td>
                        {r.asPerScope ? (
                          <span style={{ color: "#e6c168" }}>As Per Scope</span>
                        ) : (
                          <input
                            className="ls-table-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            placeholder="Enter Rate"
                            value={r.their_rate === 0 ? "" : r.their_rate}
                            onChange={(e) =>
                              updateRow(r.id, {
                                their_rate: clean(e.target.value, true),
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                            }}
                            data-testid={`row-proposed-rate-${r.id}`}
                          />
                        )}
                      </td>
                      {/* Proposed Total — auto-calculated */}
                      <td
                        style={{ color: "#f3d789", fontWeight: 500 }}
                        data-testid={`row-proposed-total-${r.id}`}
                      >
                        {r.asPerScope ? "As Per Scope" : formatINR(proposedTotal)}
                      </td>
                      <td style={{ color: "var(--ls-muted-2)", fontSize: "0.8rem" }}>
                        {r.custom ? (
                          <input
                            className="ls-table-input"
                            style={{ width: 130 }}
                            placeholder="Notes"
                            value={r.notes}
                            onChange={(e) =>
                              updateRow(r.id, { notes: e.target.value })
                            }
                          />
                        ) : (
                          r.notes || "—"
                        )}
                      </td>
                      <td>
                        {r.custom && (
                          <button
                            type="button"
                            onClick={() => removeRow(r.id)}
                            className="text-xs"
                            style={{ color: "#cfcfcf" }}
                            data-testid={`row-remove-${r.id}`}
                            title="Remove row"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Live total — only Proposed Total is shown; base rate is informational */}
          <div
            className="mt-7 grid grid-cols-1 gap-4"
            data-testid="rate-totals"
          >
            <div className="ls-card px-5 py-5 flex items-center justify-between">
              <span
                className="font-cinzel text-sm"
                style={{ letterSpacing: "0.16em", color: "var(--ls-muted)" }}
              >
                Proposed Total
              </span>
              <span
                className="gold-text font-cinzel"
                style={{ fontSize: "1.4rem", letterSpacing: "0.04em" }}
                data-testid="total-proposed"
              >
                {formatINR(totalProposed)}
              </span>
            </div>
          </div>
        </div>

        {/* ENQUIRY FORM + LIVE ESTIMATE */}
        <div
          className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 ls-fade-up"
          data-testid="enquiry-section"
        >
          <div className="lg:col-span-5">
            <div className="ls-card-strong px-6 sm:px-8 py-8 lg:sticky lg:top-28">
              <h3
                className="font-cinzel gold-text"
                style={{
                  fontSize: "1.4rem",
                  letterSpacing: "0.08em",
                }}
                data-testid="estimate-title"
              >
                Live Estimate
              </h3>
              <div className="mt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span style={{ color: "var(--ls-muted)" }}>
                    Proposed Total
                  </span>
                  <span
                    className="gold-text font-cinzel"
                    style={{ fontSize: "1.4rem" }}
                    data-testid="estimate-proposed"
                  >
                    {formatINR(totalProposed)}
                  </span>
                </div>
              </div>
              <p
                className="mt-5 text-[0.8rem] leading-relaxed"
                style={{ color: "var(--ls-muted-2)" }}
              >
                Rate will vary as per final discussion. We are here to help you.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="ls-card-strong px-6 sm:px-8 py-8">
              <h2
                className="font-cinzel gold-text"
                style={{
                  fontSize: "1.6rem",
                  letterSpacing: "0.08em",
                }}
                data-testid="enquiry-title"
              >
                Get Tentative Rate
              </h2>
              <p className="mt-2 text-sm" style={{ color: "var(--ls-muted)" }}>
                Share your contact details — we will revert with a tailored
                proposal.
              </p>

              {submitted ? (
                <div className="mt-7 ls-success" data-testid="services-success">
                  Thanks for reaching out to Lancers Services. We will call
                  shortly with a tentative rate.
                </div>
              ) : (
                <form
                  className="mt-6 space-y-4"
                  onSubmit={onSubmit}
                  data-testid="services-form"
                  noValidate
                >
                  <div>
                    <label className="ls-label">Organization Name</label>
                    <input
                      className="ls-input"
                      name="organization_name"
                      value={form.organization_name}
                      onChange={onChangeForm}
                      placeholder="e.g. Apex Facades Pvt Ltd"
                      data-testid="services-input-organization"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="ls-label">Contact Person</label>
                      <input
                        className="ls-input"
                        name="contact_person"
                        value={form.contact_person}
                        onChange={onChangeForm}
                        placeholder="Full name"
                        data-testid="services-input-contact-person"
                      />
                    </div>
                    <div>
                      <label className="ls-label">Contact Number</label>
                      <input
                        className="ls-input"
                        name="contact_number"
                        value={form.contact_number}
                        onChange={onChangeForm}
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="services-input-number"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="ls-label">Email Address</label>
                    <input
                      className="ls-input"
                      type="email"
                      name="email_address"
                      value={form.email_address}
                      onChange={onChangeForm}
                      placeholder="you@company.com"
                      data-testid="services-input-email"
                    />
                  </div>

                  <div
                    className="flex items-center justify-between pt-2"
                    data-testid="services-add-work"
                  >
                    <span
                      className="text-[0.8rem] uppercase tracking-[0.16em]"
                      style={{ color: "var(--ls-muted-2)" }}
                    >
                      Add more work in the rate table above
                    </span>
                    <button
                      type="button"
                      onClick={addCustomRow}
                      className="ls-btn-ghost"
                      data-testid="services-add-row-btn"
                    >
                      + Add Row
                    </button>
                  </div>

                  {error && (
                    <p
                      className="text-sm"
                      style={{ color: "#f87171" }}
                      data-testid="services-error"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="ls-btn-gold w-full mt-2"
                    disabled={submitting}
                    data-testid="services-submit"
                  >
                    <span>{submitting ? "Submitting…" : "Submit"}</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
